import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Frame from "../components/Frame";
import { EVENTS } from "../lib/events";

// Placeholder gallery library — swap in real photos when ready.
// Each photo is tagged with one or more event slugs; "All" shows everything.
const GALLERY = [
  // Hotel & Airbnb
  { src: "/sunstate/gallery/pink-hotel-wide.webp", tags: ["hotel-airbnb"], caption: "Hotel Setup — Pink Romance" },
  { src: "/sunstate/gallery/pink-hotel-custom.webp", tags: ["hotel-airbnb"], caption: "Custom Pink Hotel Setup" },
  { src: "/sunstate/gallery/pink-hearts-bouquet.webp", tags: ["hotel-airbnb"], caption: "Pink Hearts & Bouquet — Hotel" },
  { src: "/sunstate/gallery/pink-roses-heart-bed.webp", tags: ["hotel-airbnb"], caption: "Pink Roses Heart on Bed" },
  { src: "/sunstate/gallery/pink-bouquet.webp", tags: ["hotel-airbnb"], caption: "Pink Bouquet Detail" },
  { src: "/sunstate/gallery/hotel-dom-perignon.jpg", tags: ["hotel-airbnb"], caption: "Hotel Setup — Dom Pérignon" },
  { src: "/sunstate/gallery/hotel-classic-bw.jpg", tags: ["hotel-airbnb"], caption: "Custom Hotel Setup — Classic Black & White" },
  { src: "/sunstate/gallery/hotel-custom.jpg", tags: ["hotel-airbnb"], caption: "Custom Hotel Setup" },
  { src: "/sunstate/gallery/hotel-red-1.jpg", tags: ["hotel-airbnb"], caption: "Hotel Setup — Romantic Red Theme" },
  { src: "/sunstate/gallery/hotel-red-2.jpg", tags: ["hotel-airbnb"], caption: "Hotel Setup — Romantic Red Detail" },
  { src: "/sunstate/gallery/hotel-red-3.jpg", tags: ["hotel-airbnb"], caption: "Hotel Celebration — Romantic Red Theme" },
  // Picnics
  { src: "/sunstate/gallery/picnic-1.jpg", tags: ["picnics"], caption: "30th Birthday Picnic — Gold Coast" },
  { src: "/sunstate/gallery/picnic-2.jpg", tags: ["picnics"], caption: "30th Birthday Picnic — Styled Setup" },
  // Home Celebrations
  { src: "/sunstate/gallery/home-pink-1.jpg", tags: ["home-celebrations"], caption: "Pink-Themed Birthday at Home" },
  { src: "/sunstate/gallery/home-pink-2.jpg", tags: ["home-celebrations"], caption: "Pink Tulip & Confetti Table Styling" },
  { src: "/sunstate/gallery/home-pink-3.jpg", tags: ["home-celebrations"], caption: "Pink Birthday Tablescape Detail" },
  { src: "/sunstate/gallery/home-pink-4.jpg", tags: ["home-celebrations"], caption: "Pink-Themed Home Celebration" },
  { src: "/sunstate/gallery/home-pink-5.jpg", tags: ["home-celebrations"], caption: "Pink-Themed Birthday Setup" },
  { src: "/sunstate/gallery/home-cocktail-1.jpg", tags: ["home-celebrations"], caption: "Home Celebration — Cocktail Glasses" },
  { src: "/sunstate/gallery/home-halloween.jpg", tags: ["home-celebrations"], caption: "Halloween-Themed 29th Birthday" },
  // Coming-soon placeholders (stock photography for illustration only)
  { src: "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=1100&q=80&auto=format&fit=crop", tags: ["corporate"], caption: "Corporate Event — Stock image (for illustration only)", isPlaceholder: true },
  { src: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=1100&q=80&auto=format&fit=crop", tags: ["proposals"], caption: "Proposal Setup — Stock image (for illustration only)", isPlaceholder: true },
  { src: "/sunstate/gallery/venue-21st-full-backdrop.webp", tags: ["venue-celebrations"], caption: "21st Birthday — Brown & Cream Balloon Backdrop" },
  { src: "/sunstate/gallery/venue-21st-client-cake.webp", tags: ["venue-celebrations"], caption: "21st Birthday — Client with Cake" },
  { src: "/sunstate/gallery/venue-21st-cake-close.webp", tags: ["venue-celebrations"], caption: "21st Birthday — Cake & Sparklers" },
  { src: "/sunstate/gallery/venue-21st-cake-angle.webp", tags: ["venue-celebrations"], caption: "21st Birthday — Brown & Cream Setup" },
];

const TABS = [{ slug: "all", title: "All" }, ...EVENTS.map((e) => ({ slug: e.slug, title: e.short }))];

export const Gallery = () => {
  const [active, setActive] = useState("all");

  const filtered = useMemo(() => {
    if (active === "all") return GALLERY;
    return GALLERY.filter((g) => g.tags.includes(active));
  }, [active]);

  return (
    <div className="sc-page sc-page-gallery" data-testid="page-gallery">
      <Frame as="section" className="sc-page-hero" testId="gallery-hero">
        <span className="sc-section-eyebrow">PORTFOLIO</span>
        <h1 className="sc-page-title">A glimpse into our world</h1>
        <p className="sc-page-sub">
          A curated selection of past Sunstate Collective setups. Use the
          tabs below to filter by event type.
        </p>
      </Frame>

      <Frame as="section" className="sc-gallery" testId="gallery-section">
        <div className="sc-gallery-tabs" role="tablist" data-testid="gallery-tabs">
          {TABS.map((t) => (
            <button
              key={t.slug}
              role="tab"
              aria-selected={active === t.slug}
              type="button"
              onClick={() => setActive(t.slug)}
              className={`sc-gallery-tab ${active === t.slug ? "is-active" : ""}`}
              data-testid={`gallery-tab-${t.slug}`}
            >
              {t.title}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <p className="sc-gallery-empty" data-testid="gallery-empty">
            New imagery for this category is coming soon.
          </p>
        ) : (
          <div className="sc-gallery-grid" data-testid="gallery-grid">
            {filtered.map((g, i) => (
              <figure
                className="sc-gallery-item"
                key={`${g.src}-${i}`}
                data-testid={`gallery-item-${i}`}
                style={{ position: "relative" }}
              >
                <div
                  className="sc-gallery-img"
                  style={{ backgroundImage: `url(${g.src})` }}
                  aria-hidden="true"
                />
                {g.isPlaceholder && (
                  <span
                    data-testid={`gallery-stock-tag-${i}`}
                    style={{
                      position: "absolute",
                      left: 10,
                      top: 10,
                      padding: "3px 8px",
                      fontSize: 9,
                      letterSpacing: "1.5px",
                      textTransform: "uppercase",
                      color: "#fff",
                      background: "rgba(34,34,34,0.62)",
                      fontStyle: "italic",
                      fontWeight: 500,
                      pointerEvents: "none",
                    }}
                  >
                    Stock image
                  </span>
                )}
                <figcaption>{g.caption}</figcaption>
              </figure>
            ))}
          </div>
        )}
      </Frame>

      <Frame as="section" className="sc-cta" testId="gallery-cta">
        <h2 className="sc-cta-title">Inspired?</h2>
        <p className="sc-cta-sub">
          Let&rsquo;s create something just as beautiful for your celebration.
        </p>
        <div className="sc-cta-btns">
          <Link to="/events" className="sc-btn-primary">
            START AN ENQUIRY
          </Link>
        </div>
      </Frame>
    </div>
  );
};

export default Gallery;
